/**
 * MYOB Integration API Service
 *
 * Unlike Xero, there are no credentials to save here. MYOB uses the OAuth
 * authorization-code flow against a single Wadakaroo developer app, so the
 * workshop consents in a browser rather than pasting a client id and secret.
 * What this service manages is that consent and what to do with it:
 *
 * - Fetch the consent URL to send the browser to
 * - Read connection status (connected / lapsed / ready to push)
 * - Load income accounts and tax codes from the workshop's own MYOB file
 * - Save which of those invoice income and tax should post to
 * - Disconnect
 *
 * No Redux — the AccountingSoftwarePage manages its own async state.
 */

import axiosInstance from './axiosInstance';

/**
 * Where to send the browser for MYOB consent.
 *
 * Returns `{ available: false }` on environments with no MYOB app registered,
 * which is a state to render, not an error to throw.
 *
 * @returns {Promise<{ available: boolean, authorizeUrl?: string, message?: string }>}
 */
export const getMyobAuthorizeUrlApi = async () => {
    const response = await axiosInstance.get('/integrations/myob/authorize-url');
    return response.data;
};

/**
 * Current MYOB connection for the authenticated organisation.
 *
 * `needsReconnect` is distinct from `connected: false` — a connection whose
 * consent lapsed needs different words on screen from one that never existed.
 * `readyToPush` means connected AND told where income and tax should land.
 *
 * @returns {Promise<MyobConnectionResponse>}
 */
export const getMyobConnectionApi = async () => {
    const response = await axiosInstance.get('/integrations/myob/connection');
    return response.data;
};

/**
 * Income accounts and tax codes from the workshop's MYOB file.
 *
 * Always resolves: a MYOB outage comes back as `{ loaded: false, message }`
 * rather than a rejected promise, because the page still has a connection to
 * show around it.
 *
 * @returns {Promise<{ loaded: boolean, needsReconnect: boolean, message?: string,
 *                     incomeAccounts: Array, taxCodes: Array }>}
 */
export const getMyobReferenceDataApi = async () => {
    const response = await axiosInstance.get('/integrations/myob/reference-data');
    return response.data;
};

/**
 * Choose where invoice income and tax post to in MYOB. Both are UIDs from the
 * workshop's own file — there is no portable default the way Xero has "200".
 *
 * @param {{ incomeAccountUid: string, incomeAccountName: string,
 *           taxCodeUid: string, taxCode: string }} defaults
 * @returns {Promise<MyobConnectionResponse>}
 */
export const saveMyobDefaultsApi = async (defaults) => {
    const response = await axiosInstance.put('/integrations/myob/defaults', defaults);
    return response.data;
};

/**
 * Forget the connection. Does not revoke consent at MYOB's end — the owner does
 * that from their MYOB account.
 *
 * @returns {Promise<MyobConnectionResponse>}
 */
export const disconnectMyobApi = async () => {
    const response = await axiosInstance.delete('/integrations/myob/connection');
    return response.data;
};

const myobApi = {
    getMyobAuthorizeUrlApi,
    getMyobConnectionApi,
    getMyobReferenceDataApi,
    saveMyobDefaultsApi,
    disconnectMyobApi,
};

export default myobApi;
